import { useCallback, useEffect, useRef, useState } from "react";
import { TriggeredHook } from "sonobello.utilities.react";

import { LeadResult } from "../../../../Models/Lead";
import CreateOrUpdateContactResultResponse from "../Types/CreateOrUpdateContactResultResponse";
import ILegacyCreateOrUpdateContactResultRequest from "../Types/ILegacyCreateOrUpdateContactResultRequest";
import UpdateContactResultRequest, { UpdateContactResultRequestPayload } from "../Types/UpdateContactResultRequest";

type DelegateHook = TriggeredHook<CreateOrUpdateContactResultResponse, UpdateContactResultRequest>;

/**
 * A decorator for the {@link DelegateHook} that provides translation between legacy objects and naming
 * conventions to the latest {@link UpdateContactResultRequest} and {@link CreateOrUpdateContactResultResponse}
 * patterns expected by the business.
 */
const BuildUsePutLeadResultLegacyWrapper: (
  useDelegate: DelegateHook
) => TriggeredHook<LeadResult, ILegacyCreateOrUpdateContactResultRequest, ILegacyCreateOrUpdateContactResultRequest> =
  useDelegate => () => {
    const [wrapperResponse, setWrapperResponse] = useState<LeadResult | null>(null);

    const { execute, result, isLoading: isDelegateLoading, error } = useDelegate();
    const requestRef = useRef<ILegacyCreateOrUpdateContactResultRequest | null>(null);

    const wrapperExecute = useCallback((request: ILegacyCreateOrUpdateContactResultRequest) => {
      requestRef.current = request;
      if (!request.leadResult.id) throw new Error("Lead result id is required for put operations");
      execute(
        new UpdateContactResultRequest(
          request.leadId,
          new UpdateContactResultRequestPayload(
            request.leadResult.id,
            request.leadResult.type,
            request.leadResult.customerId,
            request.leadResult.appointmentId,
            request.leadResult.opportunityId,
            Object.entries(request.leadResult.disqualifyingReasons)
              .filter(([, isSelected]) => isSelected)
              .map(([id]) => id),
            Object.entries(request.leadResult.notOfferedProcedures)
              .filter(([, isSelected]) => isSelected)
              .map(([id]) => id),
            request.leadResult.notes,
            request.shouldDeleteOpportunity
          )
        )
      );
    }, []);

    useEffect(() => {
      if (!result || !requestRef.current) return;

      setWrapperResponse({
        id: result.id,
        type: result.typeId,
        customerId: result.patientId,
        appointmentId: result.appointmentId,
        opportunityId: result.leadId,
        disqualifyingReasons: requestRef.current.leadResult.disqualifyingReasons,
        notOfferedProcedures: requestRef.current.leadResult.notOfferedProcedures,
        notes: requestRef.current.leadResult.notes
      });
    }, [result]);

    return {
      execute: wrapperExecute,
      result: wrapperResponse,
      error: error ? requestRef.current : null,
      isLoading: isDelegateLoading
    };
  };

export default BuildUsePutLeadResultLegacyWrapper;
